import React, { useRef, useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import { IconUpload, IconTrash, IconSignature } from '@tabler/icons-react';
import { useMySignature, useUploadSignature, useDeleteSignature } from '../../hooks/usePayroll';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';

export default function PayrollSignature() {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { data: signature, isLoading } = useMySignature();
  const uploadSignature = useUploadSignature();
  const deleteSignature = useDeleteSignature();

  const hasSignature = signature && signature.image_url;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Only PNG or JPEG images are accepted.');
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast.error('Please select a signature image first.');
      return;
    }
    uploadSignature.mutate(selectedFile, {
      onSuccess: () => {
        toast.success('Signature uploaded successfully.');
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: () => {
        toast.error('Failed to upload signature. Please try again.');
      },
    });
  };

  const handleRemove = () => {
    deleteSignature.mutate(undefined, {
      onSuccess: () => {
        toast.success('Signature removed.');
        setConfirmRemove(false);
      },
      onError: () => {
        toast.error('Failed to remove signature.');
        setConfirmRemove(false);
      },
    });
  };

  return (
    <>
      <Breadcrumb title="My Signature" path="Payroll / My Signature" />

      <div className="max-w-[600px]">
        {/* Current Signature */}
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
          <h2 className="text-[16px]/[20px] font-semibold mb-5">Current Signature</h2>
          <p className="text-text-secondary text-sm mb-20">
            This signature will appear on payslips when you are listed as a signatory.
            If no signature is uploaded, your name and title will be used instead.
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center h-[120px] bg-bg-light rounded-lg border border-border">
              <p className="text-text-secondary text-sm">Loading...</p>
            </div>
          ) : hasSignature ? (
            <div className="flex flex-col items-start gap-15">
              <div className="bg-white rounded-lg border border-border p-15 w-full">
                <img
                  src={signature.image_url}
                  alt="My Signature"
                  className="max-h-[120px] max-w-full object-contain"
                />
              </div>
              <p className="text-xs text-text-secondary">
                Last updated: {new Date(signature.updated_at).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })}
              </p>
              <button
                onClick={() => setConfirmRemove(true)}
                className="btn btn-outline-danger flex items-center gap-5"
              >
                <IconTrash size={16} />
                Remove Signature
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[120px] bg-bg-light rounded-lg border border-dashed border-border gap-10">
              <IconSignature size={32} className="text-text-secondary opacity-50" />
              <p className="text-text-secondary text-sm">No signature uploaded yet</p>
            </div>
          )}
        </div>

        {/* Upload New Signature */}
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
          <h2 className="text-[16px]/[20px] font-semibold mb-5">
            {hasSignature ? 'Replace Signature' : 'Upload Signature'}
          </h2>
          <p className="text-text-secondary text-sm mb-20">
            Upload a clear image of your handwritten signature. PNG or JPEG, max 5MB.
            Reuploading will replace your current signature.
          </p>

          {/* Preview */}
          {previewUrl && (
            <div className="bg-white rounded-lg border border-border p-15 mb-15">
              <p className="text-xs text-text-secondary mb-10">Preview:</p>
              <img
                src={previewUrl}
                alt="Signature preview"
                className="max-h-[100px] max-w-full object-contain"
              />
            </div>
          )}

          <div className="flex flex-col gap-10">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center gap-10 border-2 border-dashed border-border rounded-lg p-20 cursor-pointer hover:border-primary transition-colors bg-bg-light"
            >
              <IconUpload size={20} className="text-text-secondary" />
              <span className="text-text-secondary text-sm">
                {selectedFile ? selectedFile.name : 'Click to select PNG or JPEG'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploadSignature.isPending}
              className="btn btn-primary flex items-center gap-5 w-fit"
            >
              <IconUpload size={16} />
              {uploadSignature.isPending ? 'Uploading...' : 'Upload Signature'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={handleRemove}
        isLoading={deleteSignature.isPending}
        title="Remove Signature"
        message="Are you sure you want to remove your signature? Your name and title will be used as a fallback on payslips."
        confirmLabel="Remove Signature"
        loadingLabel="Removing..."
        headerIcon={<IconSignature size={32} className="text-danger" />}
      />
    </>
  );
}
